import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Wind, Sprout, Heart, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import Marquee from "../components/Marquee";

const MANIFESTO = [
  {
    n: "01",
    title: "Presenza e Intenzione",
    text: "Il tocco non è meccanico, il silenzio è parte della cura. Ogni gesto nasce da un'intenzione chiara e abita il momento presente.",
  },
  {
    n: "02",
    title: "Il Corpo come Luogo del Sentire",
    text: "Risvegliare il corpo per attraversare le emozioni, dargli voce e lasciar andare ciò che trattiene.",
  },
  {
    n: "03",
    title: "Integrazione",
    text: "Mente, corpo, respiro ed energia: un unico sistema vivo e intelligente che chiede di essere ascoltato per intero.",
  },
  {
    n: "04",
    title: "Leggerezza come Qualità",
    text: "Fare le cose con leggerezza, portando il cento per cento della presenza. Fluidità, mai irrigidimento.",
  },
];

const METODO = [
  {
    icon: Wind,
    bg: "bg-lavanda-mist",
    fg: "text-lavanda",
    title: "Respira",
    text: "Libera le tensioni e ritrova il tuo ritmo naturale attraverso il respiro consapevole.",
  },
  {
    icon: Sprout,
    bg: "bg-sage-mist",
    fg: "text-sage",
    title: "Nutri",
    text: "Alimenta il corpo e l'anima con cibi scelti con cura, nel rispetto della tua unicità.",
  },
  {
    icon: Heart,
    bg: "bg-earth-mist",
    fg: "text-earth",
    title: "Senti",
    text: "Accogli le vibrazioni del tuo essere e connettiti con la tua essenza più autentica.",
  },
];

const AREE = [
  {
    img: "/assets/massaggio-1.jpg",
    title: "Benessere Corporeo",
    text: "Massaggio olistico, 5 Elementi e riflessologia per sciogliere le tensioni.",
  },
  {
    img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1200&auto=format&fit=crop",
    title: "Benessere Meditativo",
    text: "Respiro consapevole, yoga dolce e meditazione per quietare la mente.",
  },
  {
    img: "/assets/femminile-loto.jpg",
    title: "Benessere Femminile",
    text: "Un percorso dedicato al bacino, al pavimento pelvico e alla ciclicità.",
  },
  {
    img: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?q=80&w=1200&auto=format&fit=crop",
    title: "Cucina Consapevole",
    text: "Laboratori di cucina ayurvedica e mindful eating per nutrirsi con presenza.",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const txtY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden pt-36 pb-20">
        <div className="pointer-events-none absolute -left-40 top-20 h-[34rem] w-[34rem] rounded-full bg-sage-soft/50 blur-3xl" />
        <div className="pointer-events-none absolute -right-40 bottom-0 h-[30rem] w-[30rem] rounded-full bg-lavanda-soft/50 blur-3xl" />

        <div className="mx-auto grid max-w-6xl items-center gap-16 px-6 lg:grid-cols-[1.15fr_0.85fr]">
          <motion.div style={{ y: txtY, opacity: fade }}>
            <Reveal y={16}>
              <Eyebrow data-testid="hero-eyebrow">Artemisia · Spazio di Benessere Olistico</Eyebrow>
            </Reveal>
            <h1 className="mt-7 font-serif text-[13.5vw] leading-[1.02] text-ink sm:text-7xl lg:text-[5.2rem]" data-testid="hero-title">
              <MaskedLine delay={0.15}>Benessere,</MaskedLine>
              <MaskedLine delay={0.28}>Energia,</MaskedLine>
              <MaskedLine delay={0.41}>
                <span className="italic text-sage">Armonia</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.55} y={20}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-ink/65" data-testid="hero-subtitle">
                Ritrova la tua leggerezza interiore. Yoga, massaggi, meditazione
                e cucina consapevole: un cammino di presenza insieme ad Agnese Caon.
              </p>
            </Reveal>
            <Reveal delay={0.7} y={20}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link
                  to="/contatti"
                  data-testid="hero-cta-prenota"
                  className="rounded-full bg-[linear-gradient(135deg,#4F645B,#7A9488)] px-8 py-4 text-sm font-medium text-cream shadow-[0_12px_32px_rgba(79,100,91,0.25)] transition-transform duration-500 hover:scale-[1.04]"
                >
                  Prenota un Trattamento
                </Link>
                <Link
                  to="/servizi"
                  data-testid="hero-cta-scopri"
                  className="group flex items-center gap-2 text-sm font-medium text-sage-deep"
                >
                  Scopri il Metodo
                  <span className="block h-px w-8 bg-sage transition-all duration-500 group-hover:w-14" />
                </Link>
              </div>
            </Reveal>
          </motion.div>

          <motion.div style={{ y: imgY }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, borderRadius: "46% 54% 52% 48% / 48% 46% 54% 52%" }}
              animate={{ opacity: 1, scale: 1, borderRadius: "3rem" }}
              transition={{ duration: 1.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden shadow-[0_32px_80px_rgba(48,51,48,0.14)]"
            >
              <motion.img
                src="/assets/agnese-yoga.jpg"
                alt="Agnese Caon in pratica yoga"
                data-testid="hero-image"
                className="aspect-[4/5] w-full object-cover"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-8 -left-6 rounded-[2rem] bg-cream-soft/80 px-7 py-5 shadow-[0_12px_32px_rgba(48,51,48,0.1)] backdrop-blur-xl sm:-left-12"
            >
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-sage">Il Metodo</p>
              <p className="mt-1.5 font-serif text-lg italic text-ink">Respira · Nutri · Senti</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* ASCOLTO */}
      <section className="mx-auto max-w-6xl px-6 pt-32">
        <div className="grid items-center gap-16 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Lo Spazio</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-ink sm:text-5xl" data-testid="ascolto-title">
              Artemisia è uno spazio di <span className="italic text-sage">ascolto</span> e presenza.
            </h2>
            <p className="mt-7 max-w-md text-base leading-relaxed text-ink/65">
              Fare le cose con leggerezza significa esserci davvero. Il nostro approccio
              non è una fuga, ma un ritorno consapevole al corpo, al respiro e al cuore,
              attraverso la cura e il respiro.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:-mt-10">
            <div className="overflow-hidden rounded-[3rem] lg:rotate-2 transition-transform duration-700 hover:rotate-0">
              <img src="/assets/pietre.jpg" alt="Pietre in equilibrio" className="aspect-[5/4] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="mx-auto max-w-6xl px-6 pt-36">
        <Reveal>
          <Eyebrow>Il Manifesto</Eyebrow>
          <h2 className="mt-6 max-w-xl font-serif text-4xl leading-tight sm:text-5xl" data-testid="manifesto-title">
            Quattro capitoli, <span className="italic text-lavanda">una sola via</span>
          </h2>
        </Reveal>
        <div className="mt-16">
          {MANIFESTO.map((c, i) => (
            <Reveal key={c.n} delay={0.06 * i}>
              <div
                data-testid={`manifesto-chapter-${c.n}`}
                className={`group flex flex-col gap-4 border-t border-ink/8 py-12 transition-colors duration-500 sm:flex-row sm:items-baseline sm:gap-16 ${
                  i % 2 === 1 ? "sm:pl-24" : ""
                }`}
              >
                <span className="font-serif text-5xl italic text-sage/30 transition-colors duration-500 group-hover:text-sage sm:text-6xl">
                  {c.n}
                </span>
                <div>
                  <h3 className="font-serif text-2xl text-ink sm:text-3xl">{c.title}</h3>
                  <p className="mt-3 max-w-lg text-base leading-relaxed text-ink/60">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* METODO */}
      <section className="mt-32 bg-sage-mist/60 py-28">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal className="max-w-2xl">
            <Eyebrow>Il Metodo Artemisia</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl" data-testid="metodo-title">
              Un percorso integrato per ritrovare l'equilibrio
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ink/60">
              Tre movimenti fondamentali del benessere, intrecciati in ogni trattamento e incontro.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {METODO.map((m, i) => (
              <Reveal key={m.title} delay={0.1 * i}>
                <div
                  data-testid={`metodo-card-${m.title.toLowerCase()}`}
                  className="group h-full rounded-[2.5rem] bg-cream-soft p-9 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_56px_rgba(48,51,48,0.1)]"
                >
                  <div className={`flex h-14 w-14 items-center justify-center rounded-full ${m.bg} ${m.fg} transition-transform duration-500 group-hover:scale-110`}>
                    <m.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-7 font-serif text-2xl">{m.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AREE */}
      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Le Aree di Lavoro</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl" data-testid="aree-title">
              Ogni corpo, <span className="italic text-sage">il suo cammino</span>
            </h2>
          </div>
          <Link to="/servizi" data-testid="aree-cta" className="group flex items-center gap-2 text-sm font-medium text-sage-deep">
            Tutti i trattamenti
            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {AREE.map((a, i) => (
            <Reveal key={a.title} delay={0.08 * i} className={i % 2 === 1 ? "sm:mt-14" : ""}>
              <Link
                to="/servizi"
                data-testid={`area-card-${i}`}
                className="group block overflow-hidden rounded-[2.5rem] bg-cream-soft"
              >
                <div className="overflow-hidden">
                  <img
                    src={a.img}
                    alt={a.title}
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-start justify-between gap-4 p-8">
                  <div>
                    <h3 className="font-serif text-2xl">{a.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink/60">{a.text}</p>
                  </div>
                  <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-sage transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-[3rem] bg-sage-deep px-8 py-20 text-center sm:px-16 sm:py-24" data-testid="home-cta-panel">
            <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-sage/40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-lavanda/30 blur-3xl" />
            <h2 className="relative mx-auto max-w-2xl font-serif text-4xl italic leading-tight text-cream sm:text-5xl">
              Inizia il tuo cammino verso l'armonia
            </h2>
            <p className="relative mx-auto mt-6 max-w-md text-base text-cream/70">
              Siamo qui per guidarti nella scoperta della tua versione più luminosa.
            </p>
            <Link
              to="/contatti"
              data-testid="home-cta-button"
              className="relative mt-10 inline-block rounded-full bg-cream px-9 py-4 text-sm font-semibold text-sage-deep transition-transform duration-500 hover:scale-105"
            >
              Prenota un Trattamento
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
